import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'cara_tz_banner_dismissed';
const HEADER_SELECTOR = '.main-header.fixed-top';

// Compares the account timezone preference cached by AuthContext/Profile.jsx
// (localStorage 'cara_timezone' - the server-resolved profile.timezone,
// see TIMEZONE_IMPLEMENTATION_GUIDE.md) against this device's live timezone.
// Deliberately reads localStorage directly here rather than via
// timezoneFormat.getUserTimezone() - that helper falls back to the browser's
// own timezone when nothing is cached yet, which would make this comparison
// always pass trivially instead of just skipping until a real preference exists.
const detectMismatch = () => {
  const savedTz = localStorage.getItem('cara_timezone');
  if (!savedTz) return null;

  let browserTz;
  try {
    browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
  if (!browserTz || browserTz === savedTz) return null;

  return { savedTz, browserTz };
};

const TimezoneMismatchBanner = () => {
  const { t } = useTranslation('common');
  const [mismatch, setMismatch] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  // Both Header.js and SuperadminHeader.js render a Bootstrap
  // `.main-header.fixed-top` nav, offset from the left by whatever width
  // the sidebar currently takes up (collapsed/expanded, AdminLTE-style
  // margin). There's no single app-wide reserved offset for it - individual
  // pages add their own top padding instead (e.g. Profile.jsx's
  // `padding: '6rem 1rem 2rem'`) - so this banner tracks the header's own
  // live rendered box (top/left/width) instead of assuming any particular
  // page layout, and repositions itself to sit flush under it.
  const [headerBox, setHeaderBox] = useState({ top: 0, left: 0, width: '100%' });

  const recheck = useCallback(() => {
    setMismatch(detectMismatch());
  }, []);

  useEffect(() => {
    recheck();
    // Re-check right after AuthContext/Profile.jsx update the cached
    // preference, so the banner appears/disappears without a full reload.
    window.addEventListener('cara_timezone_updated', recheck);
    return () => window.removeEventListener('cara_timezone_updated', recheck);
  }, [recheck]);

  useEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector(HEADER_SELECTOR);
      if (headerEl) {
        const rect = headerEl.getBoundingClientRect();
        setHeaderBox({ top: rect.bottom, left: rect.left, width: rect.width });
      } else {
        setHeaderBox({ top: 0, left: 0, width: window.innerWidth });
      }
    };
    measure();

    const headerEl = document.querySelector(HEADER_SELECTOR);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (headerEl && resizeObserver) resizeObserver.observe(headerEl);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [mismatch]);

  if (!mismatch || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: headerBox.top,
        left: headerBox.left,
        width: headerBox.width,
        zIndex: 1020, // just under Bootstrap's fixed-top header (z-index 1030)
        padding: '12px 24px 0',
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
    >
      <div
        role="alert"
        className="d-flex align-items-center"
        style={{
          pointerEvents: 'auto',
          background: '#EAF3FF',
          border: '1px solid #D6E7FC',
          borderRadius: 12,
          padding: '14px 20px',
          boxShadow: '0 2px 8px rgba(20, 40, 100, 0.08)',
          gap: 16,
        }}
      >
        <div
          style={{
            flex: '0 0 auto',
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(20, 40, 100, 0.15)',
          }}
        >
          <i className="bi bi-globe2" style={{ color: '#2F6FE4', fontSize: 18 }} />
        </div>

        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#0F1B3D', fontSize: 15 }}>
            {t('timezone.mismatchTitle', { ns: 'common' })}
          </div>
          <div style={{ color: '#3B4A6B', fontSize: 14 }}>
            {t('timezone.mismatchMessage', {
              ns: 'common',
              browserTz: mismatch.browserTz,
              savedTz: mismatch.savedTz,
            })}
          </div>
        </div>

        <Link
          to="/profile"
          className="btn btn-outline-primary rounded-pill"
          style={{ flex: '0 0 auto', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 14 }}
        >
          {t('timezone.updateInProfile', { ns: 'common' })}
        </Link>

        <button
          type="button"
          className="btn-close"
          aria-label={t('timezone.dismiss', { ns: 'common' })}
          onClick={handleDismiss}
          style={{ flex: '0 0 auto' }}
        />
      </div>
    </div>
  );
};

export default TimezoneMismatchBanner;
